import React from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import MapEntityCard, {
  AssignedIncidentAction,
  IncidentCardActions,
  resolveEntityType,
} from '@/components/map/MapEntityCard';

const MapMarker = ({
  type,
  data,
  onClick,
  isSelected,
  isSearchHighlighted,
  isRelatedHighlight,
  parentIncident,
  childIncidents,
  onFocusIncident,
  clusterer,
  departmentNameMap,
  // Externally-forced card open (e.g. clicking a relationship line opens
  // BOTH endpoints' cards). onInfoClose lets the parent clear that state.
  openCard,
  onInfoClose,
}) => {
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    if (isSelected || openCard) setShowInfo(true);
  }, [isSelected, openCard]);

  const entityType = React.useMemo(() => resolveEntityType(data, type), [data, type]);

  const getIncidentIcon = (severity) => {
    if (data?.map_projection && !data.map_projection.official) {
      return 'https://maps.google.com/mapfiles/ms/icons/grey-dot.png';
    }
    const s = severity?.toLowerCase();
    if (s === 'high' || s === 'critical') return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
    if (s === 'medium' || s === 'moderate') return 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png';
    return 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png';
  };

  const getTeamIcon = () => 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';

  const position = React.useMemo(() => ({
    lat: parseFloat(data.lat || data.gps_latitude),
    lng: parseFloat(data.lng || data.gps_longitude),
  }), [data.lat, data.gps_latitude, data.lng, data.gps_longitude]);

  const zIndex = React.useMemo(() => {
    if (isSelected) return 1000;
    if (isSearchHighlighted) return 950;
    if (isRelatedHighlight) return 900;
    if (type === 'team') return 500;
    const s = data.severity?.toLowerCase();
    if (s === 'high' || s === 'critical') return 400;
    if (s === 'medium' || s === 'moderate') return 300;
    return 200;
  }, [type, isSelected, isSearchHighlighted, isRelatedHighlight, data.severity]);

  const relationshipTitle = React.useMemo(() => {
    if (type !== 'incident') return undefined;
    const parts = [];
    if (parentIncident) {
      parts.push(`Root: ${parentIncident.title || parentIncident.incident_type || parentIncident.id}`);
    }
    if (childIncidents?.length > 0) {
      parts.push(`${childIncidents.length} related incident${childIncidents.length !== 1 ? 's' : ''}`);
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  }, [type, parentIncident, childIncidents]);

  if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) return null;

  const cardActions = type === 'incident' ? (
    <IncidentCardActions incidentId={data.id} />
  ) : data.active_incident_id ? (
    <AssignedIncidentAction incidentId={data.active_incident_id} />
  ) : null;

  return (
    <Marker
      position={position}
      icon={type === 'incident' ? getIncidentIcon(data.severity) : getTeamIcon()}
      opacity={type === 'team' ? 0.9 : (isRelatedHighlight ? 1 : 1.0)}
      title={relationshipTitle || (type === 'incident' ? data.title : undefined)}
      onClick={() => {
        setShowInfo(true);
        onClick?.(data);
      }}
      clusterer={clusterer}
      zIndex={zIndex}
      animation={
        (isSelected || isSearchHighlighted || isRelatedHighlight) && window.google
          ? window.google.maps.Animation.BOUNCE
          : null
      }
    >
      {showInfo && (
        <InfoWindow
          onCloseClick={() => {
            setShowInfo(false);
            onInfoClose?.(data);
          }}
        >
          <MapEntityCard
            entityType={entityType}
            data={data}
            actions={cardActions}
            departmentNameMap={departmentNameMap}
            parentIncident={parentIncident}
            childIncidents={childIncidents}
            onFocusIncident={onFocusIncident}
          />
        </InfoWindow>
      )}
    </Marker>
  );
};

export default MapMarker;
